import React, { useState, useEffect } from 'react';
import { LuPhone, LuMail, LuMapPin, LuBuilding, LuLoader, LuUser, LuUsers } from 'react-icons/lu';
import { GiChurch } from 'react-icons/gi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import { extractString, extractOrder } from '../../shared/fieldUtils';

export default function Administration({ activeSection, onSelectSection }) {
  const [team, setTeam] = useState([]);
  const [synodMembers, setSynodMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      try {
        // 1. Fetch Office Bearers & Executive Committee (office-bearers / officers-diocese)
        let snapshot = await getDocs(collection(db, 'office-bearers')).catch(() => null);
        if (!snapshot || snapshot.empty) {
          snapshot = await getDocs(collection(db, 'office_bearers')).catch(() => null);
        }
        if (!snapshot || snapshot.empty) {
          snapshot = await getDocs(collection(db, 'officers-diocese')).catch(() => null);
        }
        if (!snapshot || snapshot.empty) {
          snapshot = await getDocs(collection(db, 'officers')).catch(() => null);
        }

        if (snapshot && !snapshot.empty) {
          const list = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: extractString(data.name),
              role: extractString(data.designation || data.position || data.role, 'Office Bearer'),
              office: extractString(data.address || data.office, 'CSI Diocese of Malabar, Diocesan Office, Bank Road'),
              email: extractString(data.email),
              phone: extractString(data.mobile || data.phone || data.ph || data.contact),
              img_url: extractString(data.priestImg || data.img_url || data.image),
              order: extractOrder(data.order)
            };
          });
          list.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
          if (isMounted) setTeam(list);
        }

        // 2. Fetch Synod Committee Members (committee-synod)
        let synodSnap = await getDocs(collection(db, 'committee-synod')).catch(() => null);
        if (!synodSnap || synodSnap.empty) {
          synodSnap = await getDocs(collection(db, 'committee_synod')).catch(() => null);
        }
        if (!synodSnap || synodSnap.empty) {
          synodSnap = await getDocs(collection(db, 'synod-committee')).catch(() => null);
        }

        if (synodSnap && !synodSnap.empty) {
          const synodList = synodSnap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: extractString(data.name),
              role: extractString(data.position || data.role || data.designation, 'Synod Member'),
              email: extractString(data.email),
              ph: extractString(data.phone || data.ph || data.contact || data.mobile),
              place: extractString(data.place || data.office || data.address),
              order: extractOrder(data.order)
            };
          });
          synodList.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
          if (isMounted) setSynodMembers(synodList);
        }
      } catch (err) {
        console.error("Error fetching administration & council data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchData();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 sm:pb-20 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] sm:right-[10%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 text-center md:text-left flex flex-col md:items-start items-center">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full"></div>
          <h1 className="font-serif font-bold tracking-[0.03em] text-[#7B1E3A] text-[38px] sm:text-[50px] md:text-[58px] lg:text-[64px] leading-[1.05] uppercase">
            Administration & Governance
          </h1>
          <p className="mt-4 sm:mt-5 max-w-xl text-[#7B6B66] text-[15px] sm:text-[17px] leading-[1.6] font-normal">
            Diocesan executive officers, central secretariat, and synod delegation.
          </p>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-16 w-full space-y-20">
        
        {/* Intro Banner */}
        <div className="max-w-4xl mx-auto bg-white p-8 sm:p-10 rounded-[32px] border border-[#E8DDD7] shadow-sm text-center">
          <h2 className="text-[#7B1E3A] font-serif font-bold text-2xl sm:text-3xl mb-4">
            Diocesan Leadership & Governance
          </h2>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85]">
            The Diocesan administration and Council constitute the governing body of the CSI Diocese of Malabar, guiding the spiritual, administrative, and missional life of the Church across Northern Kerala.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-[#E8DDD7]">
            <LuLoader className="w-10 h-10 text-[#7B1E3A] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Loading Administration & Leadership...</p>
          </div>
        ) : (
          <>
            {/* Section 1: Office Bearers */}
            <div>
              <div className="text-center max-w-3xl mx-auto mb-10">
                <span className="text-[#D4A64A] font-sans font-bold text-xs uppercase tracking-[0.2em] mb-2 block">
                  EXECUTIVE LEADERSHIP
                </span>
                <h3 className="text-[#7B1E3A] font-serif font-bold text-3xl sm:text-4xl">Diocese Office Bearers</h3>
              </div>

              {team.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-[24px] border border-[#E8DDD7] p-6">
                  <p className="text-sm text-[#7B6B66]">No Office Bearers found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {team.map((member, idx) => (
                    <div key={member.id || idx} className="bg-white rounded-[28px] p-6 sm:p-8 border border-[#E8DDD7] shadow-[0_10px_30px_rgba(123,30,58,0.04)] hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row gap-6 items-start">
                      {member.img_url ? (
                        <div className="w-24 h-28 sm:w-28 sm:h-32 rounded-2xl overflow-hidden shadow-sm border border-[#E8DDD7] shrink-0 bg-[#FAF6F2]">
                          <img src={member.img_url} alt={member.name} className="w-full h-full object-cover object-top" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-[#FAF6F2] text-[#7B1E3A] flex items-center justify-center shrink-0">
                          <LuUser className="w-8 h-8" />
                        </div>
                      )}

                      <div className="flex-1 w-full">
                        <span className="text-[#D4A64A] font-sans font-bold text-xs uppercase tracking-[0.2em] mb-1.5 block">
                          {member.role}
                        </span>
                        <h4 className="font-serif font-bold text-[#7B1E3A] text-xl sm:text-2xl mb-4">
                          {member.name}
                        </h4>
                        
                        <div className="space-y-3">
                          {member.office && (
                            <div className="flex items-start gap-2.5">
                              <LuMapPin className="w-4 h-4 text-[#7B1E3A] shrink-0 mt-0.5" />
                              <span className="text-[#5F5A58] text-[14px] leading-relaxed">{member.office}</span>
                            </div>
                          )}
                          {member.email && (
                            <div className="flex items-center gap-2.5">
                              <LuMail className="w-4 h-4 text-[#7B1E3A] shrink-0" />
                              <a href={`mailto:${member.email}`} className="text-[#5F5A58] text-[14px] hover:text-[#D4A64A] transition-colors break-words">
                                {member.email}
                              </a>
                            </div>
                          )}
                          {member.phone && (
                            <div className="flex items-start gap-2.5">
                              <LuPhone className="w-4 h-4 text-[#7B1E3A] shrink-0 mt-0.5" />
                              <span className="text-[#5F5A58] text-[14px] leading-relaxed">{member.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Section 2: Synod Committee Members */}
            <div>
              <div className="text-center max-w-3xl mx-auto mb-10">
                <span className="text-[#D4A64A] font-sans font-bold text-xs uppercase tracking-[0.2em] mb-2 block">
                  SYNOD DELEGATION
                </span>
                <h3 className="text-[#7B1E3A] font-serif font-bold text-3xl">Synod Committee Members</h3>
              </div>

              {synodMembers.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-[24px] border border-[#E8DDD7] p-6">
                  <p className="text-sm text-[#7B6B66]">No Synod Committee records found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {synodMembers.map((member, idx) => (
                    <div key={member.id || idx} className="bg-white rounded-[24px] p-6 border border-[#E8DDD7] shadow-sm hover:shadow-md transition-all flex flex-col">
                      <div className="w-12 h-12 rounded-full bg-[#FAF6F2] text-[#7B1E3A] flex items-center justify-center mb-4">
                        <LuUsers className="w-6 h-6" />
                      </div>
                      <span className="text-[#D4A64A] font-sans font-bold text-xs uppercase tracking-wider mb-1 block">
                        {member.role}
                      </span>
                      <h4 className="font-serif font-bold text-[#7B1E3A] text-lg mb-3">
                        {member.name}
                      </h4>
                      <div className="space-y-2 text-xs text-[#5F5A58] mt-auto">
                        {member.place && (
                          <div className="flex items-start gap-2">
                            <LuMapPin className="w-3.5 h-3.5 text-[#D4A64A] shrink-0 mt-0.5" />
                            <span>{member.place}</span>
                          </div>
                        )}
                        {member.ph && member.ph !== "0" && member.ph !== "NA" && (
                          <div className="flex items-center gap-2">
                            <LuPhone className="w-3.5 h-3.5 text-[#D4A64A] shrink-0" />
                            <a href={`tel:${member.ph}`} className="hover:text-[#7B1E3A] transition-colors">{member.ph}</a>
                          </div>
                        )}
                        {member.email && member.email !== "NA" && member.email !== "." && (
                          <div className="flex items-center gap-2">
                            <LuMail className="w-3.5 h-3.5 text-[#D4A64A] shrink-0" />
                            <a href={`mailto:${member.email}`} className="hover:text-[#7B1E3A] transition-colors break-words">{member.email}</a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Section 3: Headquarters Banner */}
        <div className="bg-gradient-to-r from-[#7B1E3A] to-[#5A1428] rounded-[32px] p-10 sm:p-14 text-center text-white relative overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 opacity-10 transform translate-x-10 -translate-y-10 pointer-events-none">
            <LuBuilding className="w-48 h-48 text-white" />
          </div>
          <div className="relative z-10 max-w-2xl mx-auto">
            <span className="text-[#E8B86D] font-sans font-bold text-xs uppercase tracking-[0.2em] mb-2 block">
              CENTRAL ADMINISTRATION
            </span>
            <h3 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl mb-4 !text-white text-white">
              Diocesan Headquarters
            </h3>
            <p className="!text-white/90 text-white/90 text-[15px] sm:text-[16px] leading-[1.8] mb-8 font-sans">
              CSI Diocese of Malabar, Diocesan Office, Bank Road, Kozhikode, Kerala - 673001
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="tel:04952721748" 
                className="px-8 py-3.5 bg-white text-[#7B1E3A] hover:bg-[#E8B86D] hover:text-[#5A1428] font-bold text-xs tracking-wider uppercase rounded-full transition-all duration-300 shadow-md cursor-pointer"
              >
                Call Office
              </a>
              <a 
                href="mailto:csimalabardiocese@gmail.com" 
                className="px-8 py-3.5 bg-white/15 hover:bg-white/25 border border-white/40 !text-white text-white font-bold text-xs tracking-wider uppercase rounded-full transition-all duration-300 cursor-pointer"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>

        <div className="text-center pb-8">
          <button 
            onClick={() => onSelectSection('about')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white border border-[#E8DDD7] hover:border-[#D4A64A] text-[#7B1E3A] font-sans font-bold text-sm tracking-wider uppercase rounded-full transition-all duration-300 shadow-sm cursor-pointer"
          >
            Return to About Diocese
          </button>
        </div>

      </div>
    </div>
  );
}
